// Guest scanner asks for an account in two places:
//   - Add: they chose to save a card. The card stays pending until sign-in
//     succeeds on the scan page; nothing is written before that.
//   - Dismiss: they left the scan session. No cards are pending.

import type { CatalogEntry } from './phash';

export type GuestScanPromptKind = 'add' | 'dismiss';

export type GuestScanPendingAdd = {
  card: CatalogEntry;
  quantity: number;
  isFoil: boolean;
};

export type GuestScanPromptSnapshot = {
  kind: GuestScanPromptKind | null;
  pendingAdd: GuestScanPendingAdd | null;
};

const EMPTY: GuestScanPromptSnapshot = { kind: null, pendingAdd: null };

let snapshot: GuestScanPromptSnapshot = EMPTY;
let scanPageMounts = 0;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function getGuestScanPrompt(): GuestScanPromptSnapshot {
  return snapshot;
}

export function subscribeGuestScanPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function requestGuestScanAddPrompt(pending: GuestScanPendingAdd) {
  snapshot = { kind: 'add', pendingAdd: pending };
  emit();
}

/**
 * After the scan page unmounts. If account creation from Add is already
 * open (pending card, dialog hidden), don't stack a second prompt — drop
 * the unsaved card instead. Leaving the scanner is not a save.
 */
export function requestGuestScanDismissPrompt() {
  if (snapshot.pendingAdd && snapshot.kind == null) {
    snapshot = EMPTY;
    emit();
    return;
  }
  snapshot = { kind: 'dismiss', pendingAdd: null };
  emit();
}

/** Hide the dialog. An in-progress Add keeps its card for the sign-in that follows. */
export function beginGuestScanAccountCreation() {
  snapshot = {
    kind: null,
    pendingAdd: snapshot.kind === 'add' ? snapshot.pendingAdd : null,
  };
  emit();
}

export function hideGuestScanPrompt() {
  if (!snapshot.kind) return;
  snapshot = { kind: null, pendingAdd: snapshot.pendingAdd };
  emit();
}

export function clearGuestScanPrompt() {
  if (!snapshot.kind && !snapshot.pendingAdd) return;
  snapshot = EMPTY;
  emit();
}

export function takeGuestScanPendingAdd(): GuestScanPendingAdd | null {
  const pending = snapshot.pendingAdd;
  if (!pending) return null;
  snapshot = EMPTY;
  emit();
  return pending;
}

export function noteScanPageMounted(): () => void {
  scanPageMounts += 1;
  return () => {
    scanPageMounts -= 1;
  };
}

export function scanPageIsMounted(): boolean {
  return scanPageMounts > 0;
}

/** True only after a confirmed guest session has actually left the scanner. */
export function shouldPromptGuestAfterScanLeave(
  wasGuest: boolean,
  scanPageStillMounted: boolean,
): boolean {
  return wasGuest && !scanPageStillMounted;
}

export function resetGuestScanPromptForTests() {
  snapshot = EMPTY;
  scanPageMounts = 0;
  listeners.clear();
}
